import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import { useLocation } from '@reach/router'
import { initializeAndTrack } from 'gatsby-plugin-gdpr-cookies'
import { injectIntl } from 'gatsby-plugin-intl'
import CookieMenu from './CookieMenu'
import CookieBar from './CookieBar'
import { GlobalStateContext } from '../context/globalContext'
import { GlobalDispatchContext } from '../context/globalContext'

export default injectIntl(props => {
	const dispatch = useContext(GlobalDispatchContext)
	const state = useContext(GlobalStateContext)

	const data = useStaticQuery(graphql`
		query{
			allContentfulCookieBar {
				edges {
					node {
						node_locale
						text
						acceptButton
						manageButton
						policyLink {
							href
							value
							node_locale
						}
					}
				}
			}
			allContentfulCookieMenu {
				edges {
					node {
						node_locale
						acceptAllButton
						backButton
						description {
							description
						}
						category {
							catId
							alwaysActive
							name
							description {
								description
							}
							cookies {
								name
								provider
								expiration
								type
								purpose
							}
						}
						tableHeaders {
							expiration
							name
							provider
							purpose
							type
						}
						logo {
							description
							file {
								url
							}
						}
						footerTitle
						footerDescription {
							json
						}
						rejectAllButton
						saveConfirmButton
						title
						viewDetails
					}
				}
			}
		}
	`)

	const lang = props.intl.locale
    const location = useLocation()

	const cookieBarInfo = data.allContentfulCookieBar.edges.filter( d => (d.node.node_locale === lang))[0].node
	const cookieMenuInfo = data.allContentfulCookieMenu.edges.filter( d => (d.node.node_locale === lang))[0].node
	const { policyLink } = cookieBarInfo
	
	const [catAlwaysActive, setCatAlwaysActive] = useState([])
	const [openCookies, setOpenCookies] = useState(false)
	const [openMenu, setOpenMenu] = useState(false)
	const [openDetails, setOpenDetails] = useState(false)
	const [catSelected, setCatSelected] = useState('')
	const [catSwitchCheck, setCatSwitchCheck] = useState({})

	useEffect(() => {
		window[`ga-disable-${process.env.GOOGLE_ANALYTICS}`] = true
	}, [])

	const catCheck = useCallback((switchCheck) => {
		for(const cat in switchCheck) {
			if(switchCheck[cat] === true) {
				switch(cat) {
					case 'technical':
						break;
					case 'analytics':
						//Jivochat
						let jivoScript = document.createElement('script')
						jivoScript.setAttribute('src',`//code.jivosite.com/widget/${process.env.GATSBY_JIVOCHAT_ID}`)
						jivoScript.setAttribute('async', true)
						document.head.appendChild(jivoScript)

						//Google Analytics
						document.cookie = 'gatsby-gdpr-google-analytics=true'
						window[`ga-disable-${process.env.GOOGLE_ANALYTICS}`] = false
						initializeAndTrack(location)
						break;
					case 'marketing':
						break;
					case 'personalization':
						break;
					default:
						break;
				}
			}
		}
	}, [location])

	const executeAfterCookieConsent = useCallback((switchCheck) => {
		catCheck(switchCheck)
		document.cookie = 'cookiesConfirmed=true'
		document.cookie = `cookieSettings=${JSON.stringify(switchCheck)}`
		dispatch(state.changeCookieSettings(JSON.stringify(switchCheck)))
		setCatSwitchCheck({...switchCheck})
		setTimeout(() => {
			setOpenCookies(false)
			menuClose()
		}, 250)
	}, [dispatch, state, catCheck])

	useEffect(() => {
        const checkCookieConfirmation = () => {
            let cookiesConfirmed = getCookie("cookiesConfirmed")
            if (!cookiesConfirmed) {            
                setOpenCookies(true)
            } else {
				setOpenCookies(false)
			}
		}
        if(!document.cookie.match(/^(.*;)?\s*cookiesConfirmed\s*=\s*[^;]+(.*)?$/)) {
			setOpenCookies(true)
        } else {
            checkCookieConfirmation()
        }
	},[]);

	useEffect(() => {
		if(state.cookieSettings) {
			const cookieSettingsTemp = JSON.parse(state.cookieSettings)
			let catSwitchCheckTemp = {}
			let catAlwaysActiveTemp = []
			cookieMenuInfo.category.filter(cat => cat.cookies).forEach(cat => {
				catSwitchCheckTemp[cat.catId] = cat.alwaysActive || cookieSettingsTemp[cat.catId] ? true : false
				if(cat.alwaysActive || cookieSettingsTemp[cat.catId]) {
					catAlwaysActiveTemp.push(cat.catId)
				}
			})
			setCatAlwaysActive([...catAlwaysActiveTemp])
			setCatSwitchCheck({...catSwitchCheckTemp})
			if(document.cookie.match(/^(.*;)?\s*cookieSettings\s*=\s*[^;]+(.*)?$/)) {
				const cookieSetCookie = getCookie("cookieSettings")
				if(cookieSetCookie !== state.cookieSettings) {
					if(document.cookie.match(/^(.*;)?\s*cookiesConfirmed\s*=\s*[^;]+(.*)?$/)) {
						let cookiesConfirmed = getCookie("cookiesConfirmed")
						if(cookiesConfirmed) {
							executeAfterCookieConsent(catSwitchCheckTemp)
						} else {
							catCheck(catSwitchCheckTemp)
						}
					} else {
						catCheck(catSwitchCheckTemp)
					}
				} else {
					catCheck(catSwitchCheckTemp)
				}
			}
		} else {
			if(!document.cookie.match(/^(.*;)?\s*cookieSettings\s*=\s*[^;]+(.*)?$/)) {
				let catSwitchCheckTemp = {}
				cookieMenuInfo.category.filter(cat => cat.cookies).forEach(cat => {
					catSwitchCheckTemp[cat.catId] = cat.alwaysActive ? true : false
				})
				setCatSwitchCheck({...catSwitchCheckTemp})
			} else {
				const cookie = getCookie("cookieSettings")
				const cookieSettings = JSON.parse(cookie)
				setCatSwitchCheck({...cookieSettings})
				executeAfterCookieConsent(cookieSettings)			
			}
	
			let catAlwaysActiveTemp = []
			cookieMenuInfo.category.filter(cat => cat.cookies).forEach(cat => {
				if(cat.alwaysActive) {
					catAlwaysActiveTemp.push(cat.catId)
				}
			})
			setCatAlwaysActive(catAlwaysActiveTemp)
		}
	},[state, cookieMenuInfo.category, executeAfterCookieConsent, catCheck])

    const getCookie = (cname) => {
        let name = cname + "="
        let ca = document.cookie.split(';')
        for(let i = 0; i < ca.length; i++) {
			let c = ca[i]
			while (c.charAt(0) === ' ') {
				c = c.substring(1)
			}
			if (c.indexOf(name) === 0) {
				return c.substring(name.length, c.length)
			}
        }
        return "";
    }


	const manage = () => {
		setOpenMenu(true)
	}

	const menuClose = () => {
		setOpenMenu(false)
		setCatSelected('')
		setOpenDetails(false)
	}

	const acceptAll = () => {
		let catSwitchCheckTemp = catSwitchCheck
		for(const cat in catSwitchCheckTemp) {
			catSwitchCheckTemp[cat] = true
		}
		executeAfterCookieConsent(catSwitchCheckTemp)
	}

	const save = () => {
		executeAfterCookieConsent(catSwitchCheck)
	}

	const rejectAll = () => {
		let catSwitchCheckTemp = catSwitchCheck
		for(const cat in catSwitchCheckTemp) {
			if(!catAlwaysActive.includes(cat)) {
				catSwitchCheckTemp[cat] = false
			}
		}
		executeAfterCookieConsent(catSwitchCheckTemp)
	}

	const openViewDetails = (e) => {
		const catId = e.target.getAttribute('data-id')
		setCatSelected({...cookieMenuInfo.category.filter(cat => cat.catId === catId)[0]})
		setOpenDetails(true)
	}

	const closeViewDetails = () => {
		setCatSelected('')
		setOpenDetails(false)
	}

	const handleSwitchToggle = (catId, checked) => {
		let catSwitchCheckTemp = catSwitchCheck
		catSwitchCheckTemp[catId] = checked
		setCatSwitchCheck({...catSwitchCheckTemp})
	}	

    return (
		<>
			<CookieBar 
				openCookies={openCookies}
				cookieBarInfo={cookieBarInfo}
				manage={manage}
				acceptAll={acceptAll}
				policyLink={policyLink}
			/>
			<CookieMenu 
				openMenu={openMenu}
				menuClose={menuClose}
				cookieMenuInfo={cookieMenuInfo}
				acceptAll={acceptAll}
				save={save}
				rejectAll={rejectAll}
				catSwitchCheck={catSwitchCheck}
				handleSwitchToggle={handleSwitchToggle}
				openDetails={openDetails}
				openViewDetails={openViewDetails}
				closeViewDetails={closeViewDetails}
				catSelected={catSelected}
				catAlwaysActive={catAlwaysActive}
			/>
		</>
    )
})

